/**
 * Scrollytelling - Alpine component for the "hyperdigital/scrollytelling" content element.
 *
 * Watches the text steps with an IntersectionObserver whose root margin shrinks the viewport to a
 * band around its vertical centre; the step inside that band is "active" and the sticky media
 * column shows its medium. No scroll listener, no layout reads, works with keyboard scrolling and
 * find-in-page. Videos of the active step play, all others pause.
 */
document.addEventListener('alpine:init', () => {
    Alpine.data('scrollytelling', () => ({
        active: 0,
        _observer: null,
        _resizeObserver: null,

        init() {
            const steps = Array.from(this.$root.querySelectorAll('[data-scrolly-step]'));
            if (!steps.length || !('IntersectionObserver' in window)) {
                return;
            }

            this._observer = new IntersectionObserver((entries) => {
                // Prefer the entry that is intersecting; with several (very short steps) take the last one in DOM order.
                const visible = entries.filter((entry) => entry.isIntersecting);
                if (!visible.length) {
                    return;
                }
                const index = Number.parseInt(visible[visible.length - 1].target.dataset.scrollyStep, 10);
                if (!Number.isNaN(index) && index !== this.active) {
                    this.active = index;
                    this.syncVideos();
                }
            }, {
                // Only the middle 30% of the viewport counts, so exactly one step is active at a time.
                rootMargin: '-35% 0px -35% 0px',
                threshold: 0,
            });

            steps.forEach((step) => this._observer.observe(step));
            this.syncVideos();
            this.setupConnector();
        },

        /**
         * Turns the CSS straight line into a curve through the step dots.
         *
         * The dots are the anchors, so the curve follows whatever the layout actually did - it
         * bends more between far apart steps and stays gentle between close ones. Everything is
         * measured, never assumed, and the path is rebuilt whenever the list is resized (a font
         * loading, a viewport change, an image finally arriving). If any of it fails the straight
         * line the stylesheet drew is still there, which is why "has-curve" is only set once a
         * path really exists.
         */
        setupConnector() {
            const list = this.$root.querySelector('[data-scrolly-connector="curve"], [data-scrolly-connector="zigzag"]');
            const svg = this.$refs.connector;
            if (!list || !svg || typeof ResizeObserver === 'undefined') {
                return;
            }

            const draw = () => {
                const dots = Array.from(list.querySelectorAll('[data-scrolly-dot]'));
                if (dots.length < 2) {
                    return;
                }

                const box = list.getBoundingClientRect();
                const points = dots.map((dot) => {
                    const rect = dot.getBoundingClientRect();
                    return {
                        x: rect.left - box.left + rect.width / 2,
                        y: rect.top - box.top + rect.height / 2,
                    };
                });

                // A cubic through every dot. Where consecutive dots sit on top of each other (the
                // plain curve, one gutter down the side) there is nothing to interpolate, so the
                // control points are pushed sideways in alternating directions to give the line a
                // shape at all. Where the dots already differ in x - the zig-zag layout puts them
                // on alternating sides - the horizontal travel is the shape, and control points
                // held at each end's own x turn the corners into a smooth S instead.
                const sway = Math.max(6, Math.min(18, box.width * 0.02));
                let d = `M ${points[0].x} ${points[0].y}`;
                for (let i = 1; i < points.length; i++) {
                    const from = points[i - 1];
                    const to = points[i];
                    const midY = (from.y + to.y) / 2;
                    if (Math.abs(to.x - from.x) > 4) {
                        d += ` C ${from.x} ${midY}, ${to.x} ${midY}, ${to.x} ${to.y}`;
                    } else {
                        const bend = i % 2 === 0 ? -sway : sway;
                        d += ` C ${from.x + bend} ${midY}, ${to.x + bend} ${midY}, ${to.x} ${to.y}`;
                    }
                }

                svg.setAttribute('viewBox', `0 0 ${Math.max(1, svg.clientWidth)} ${Math.max(1, list.offsetHeight)}`);
                svg.innerHTML = `<path class="scrolly-connector-path" d="${d}" />`;
                list.classList.add('has-curve');
            };

            draw();
            this._resizeObserver = new ResizeObserver(draw);
            this._resizeObserver.observe(list);
        },

        isActive(index) {
            return this.active === index;
        },

        syncVideos() {
            this.$root.querySelectorAll('[data-scrolly-media] video').forEach((video) => {
                const holder = video.closest('[data-scrolly-media]');
                const isActive = holder && Number.parseInt(holder.dataset.scrollyMedia, 10) === this.active;
                if (isActive) {
                    const playing = video.play();
                    if (playing && typeof playing.catch === 'function') {
                        playing.catch(() => { /* autoplay blocked - poster stays */ });
                    }
                } else if (!video.paused) {
                    video.pause();
                }
            });
        },

        destroy() {
            if (this._observer) {
                this._observer.disconnect();
            }
            if (this._resizeObserver) {
                this._resizeObserver.disconnect();
            }
        },
    }));
});
